import { Button, Divider, Modal } from "antd";
import CIQDatePicker from "components/custom-date-picker";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import "./style.scss";
import TextArea from "antd/lib/input/TextArea";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

type PopupProps = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  onOk: (props: {
    actualDate: Dayjs | null;
    reason: string | null;
  }) => Promise<void>;
  disabledDate?: ((current: Dayjs) => boolean) | undefined;
  milestoneName: string;
  existingActualDate: string | null;
  existingReason: string | null;
  title?: string;
  confirmationPrompt?: string;
};
function EarlyStartModelConfirm({
  isModalOpen,
  setIsModalOpen,
  onOk,
  disabledDate,
  milestoneName,
  existingActualDate,
  existingReason,
  title = "Release Material Early",
  confirmationPrompt = "Are you sure you want to release this Material before the linked predecessors are completed?"
}: PopupProps) {
  const [loading, setLoading] = useState(false);
  const [isValid, setValid] = useState(false);

  const [inputData, setInputData] = useState({
    actualDate: existingActualDate
      ? DateUtils.dateTimeObj(existingActualDate)
      : null,
    reason: existingReason
  });

  useEffect(() => {
    if (
      inputData.reason &&
      /\S/.test(inputData.reason) &&
      inputData.actualDate
    ) {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [inputData.actualDate, inputData.reason]);

  const handleOk = async () => {
    setLoading(true);
    await onOk(inputData);
    setLoading(false);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleClearActualDate = async () => {
    setLoading(true);
    await onOk({ actualDate: null, reason: null });
    setLoading(false);
    setIsModalOpen(false);
  };

  return (
    <Modal
      title={title}
      className="early-start-model"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      width={536}
      data-testid="index-modal"
    >
      <div className="flex flex-col w-full space-x-1 space-y-3 p-2">
        {!existingActualDate && (
          <div className="text-xs">
            <div className="pb-3">{confirmationPrompt}</div>
            <ul>
              <li>If yes, please enter the below details and click Confirm.</li>
              <li>If no, click Cancel.</li>
            </ul>
          </div>
        )}
        <div>
          <div className="form-item-required">Reason</div>
          <TextArea
            rows={2}
            placeholder="Please enter reason to start early."
            style={{ resize: "none" }}
            required
            value={inputData.reason || undefined}
            onChange={(e) => {
              setInputData((pre) => ({ ...pre, reason: e.target.value }));
            }}
            disabled={loading}
          />
        </div>
        <div>
          <div className="form-item-required pb-1">
            Actual Date ({milestoneName})
          </div>
          <CIQDatePicker
            className="w-[168px]"
            format={DATE_FORMAT_MMDDYYYY}
            value={inputData.actualDate}
            onChange={(date: Dayjs | null) => {
              if (date) setInputData((pre) => ({ ...pre, actualDate: date }));
            }}
            disabledDate={disabledDate}
            disabled={loading}
            allowClear={false}
            placeholder="Set Actual Date"
          />
        </div>
      </div>
      <Divider className="m-0" />
      <div className="flex justify-end space-x-4 p-2">
        {existingActualDate && (
          <Button
            className="w-24"
            onClick={handleClearActualDate}
            disabled={loading}
            data-testid="index-button-clear-all"
          >
            Clear All
          </Button>
        )}
        <Button
          key="cancel"
          className="w-24"
          onClick={handleCancel}
          disabled={loading}
          data-testid="index-button-cancel"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={!isValid}
          loading={loading}
          onClick={handleOk}
          className="w-24"
          data-testid="index-button-update-confirm"
        >
          {existingActualDate ? "Update" : "Confirm"}
        </Button>
      </div>
    </Modal>
  );
}
export default EarlyStartModelConfirm;
